import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /**
   * Set from `showDialog` until the dialog has finished closing, however it closed.
   * @type {object | null}
   */
  #session = null;

  /** @type {Promise<void> | null} */
  #closing = null;

  /**
   * Where focus goes back to when the dialog closes and the browser has nowhere to put it.
   * @type {HTMLElement | null}
   */
  returnFocusTarget = null;

  /**
   * The element in the dialog that last had focus.
   * @type {Element | null}
   */
  #lastFocused = null;

  /** Puts focus back in the dialog when a re-render of its content removes the focused element. */
  #focusGuard = new MutationObserver(() => this.#keepFocusInside());

  connectedCallback() {
    super.connectedCallback();

    // `cancel` and `close` do not bubble, so listen in the capture phase. Both fire however the dialog was
    // asked to close, including an Escape press that never reached this element's keydown listener.
    this.addEventListener('cancel', this.#handleCancel, { capture: true });
    this.addEventListener('close', this.#handleNativeClose, { capture: true });
    this.addEventListener('focusin', this.#trackFocus);
    this.addEventListener('pointerdown', this.#forgetFocus);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('cancel', this.#handleCancel, { capture: true });
    this.removeEventListener('close', this.#handleNativeClose, { capture: true });
    this.removeEventListener('focusin', this.#trackFocus);
    this.removeEventListener('pointerdown', this.#forgetFocus);

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    // A dialog removed while open must not leave the page locked.
    if (this.#session) {
      this.#focusGuard.disconnect();
      unlockPageScroll(this);
      this.#session = null;
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const active = document.activeElement;
    this.returnFocusTarget = active instanceof HTMLElement && active !== document.body ? active : null;

    this.#session = {};
    this.#lastFocused = null;
    lockPageScroll(this);

    dialog.showModal();
    this.#focusGuard.observe(dialog, { childList: true, subtree: true });
    this.dispatchEvent(new DialogOpenEvent());

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      if (!this.#session) return;
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   *
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;
    if (this.#closing) return this.#closing;

    this.#closing = this.#animateClose(dialog).finally(() => {
      this.#closing = null;
    });

    return this.#closing;
  };

  /**
   * Plays the closing animation, then closes the dialog.
   *
   * @param {HTMLDialogElement} dialog - The dialog element.
   */
  async #animateClose(dialog) {
    const session = this.#session;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // Closed some other way during the animation: `#finishClose` has already run for this opening.
    if (this.#session !== session) return;

    // Unlock first so the page is back at its scroll position when focus returns to the opener.
    unlockPageScroll(this);
    dialog.close();
    this.#finishClose();
  }

  /**
   * Runs once per opening, after the dialog has closed by any route.
   */
  #finishClose() {
    if (!this.#session) return;

    const { dialog } = this.refs;

    this.#session = null;
    this.#lastFocused = null;
    this.#focusGuard.disconnect();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    dialog.classList.remove('dialog-closing');
    unlockPageScroll(this);

    this.#restoreFocus();

    this.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * An Escape press (or other close request) that did not go through `#handleKeyDown`, for example when
   * focus had fallen to <body>. Run the same close as the close button instead of the browser's instant one.
   * When the browser does not let the request be cancelled, `#handleNativeClose` cleans up after it.
   *
   * @param {Event} event - The cancel event.
   */
  #handleCancel = (event) => {
    if (event.target !== this.refs.dialog || !event.cancelable) return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * The dialog closed without `closeDialog` finishing (the browser closed it, or code called `close()`).
   *
   * @param {Event} event - The close event.
   */
  #handleNativeClose = (event) => {
    // Ignore a stale event when the dialog was opened again before it fired
    if (event.target !== this.refs.dialog || this.refs.dialog.open) return;

    this.#finishClose();
  };

  /**
   * @param {FocusEvent} event - The focusin event.
   */
  #trackFocus = (event) => {
    if (event.target instanceof Element && this.refs.dialog?.contains(event.target)) {
      this.#lastFocused = event.target;
    }
  };

  /** A pointer press moves focus where the user wants it (or nowhere), so stop guarding the old spot. */
  #forgetFocus = () => {
    this.#lastFocused = null;
  };

  /**
   * Moves focus back into the open dialog when a re-render of its content took it away, e.g. the cart
   * drawer going from its empty to its filled markup right after it opened replaces the close button that
   * `showModal()` had focused, which leaves focus on <body> (WCAG 2.4.3).
   */
  #keepFocusInside() {
    const { dialog } = this.refs;
    const lost = this.#lastFocused;
    const active = document.activeElement;

    if (!dialog.open || !lost) return;
    if (active && active !== document.body && active !== document.documentElement) return;

    const target = dialog.contains(lost) && isFocusableNow(lost) ? lost : getFocusableElements(dialog)[0];

    if (target) {
      target.focus({ preventScroll: true });
    } else {
      if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
      dialog.focus({ preventScroll: true });
    }
  }

  /**
   * The browser returns focus to the element that had it when the dialog opened. When that was nothing
   * (Safari does not focus a clicked button) or it has gone, use `returnFocusTarget`, or the target of the
   * dialog it was in (quick add closes as the cart drawer opens).
   */
  #restoreFocus() {
    const { dialog } = this.refs;
    const active = document.activeElement;

    if (active && active !== document.body && active !== document.documentElement && !dialog.contains(active)) {
      return;
    }

    let target = this.returnFocusTarget;
    const visited = new Set();

    while (target && !visited.has(target)) {
      visited.add(target);
      if (isFocusableNow(target)) {
        target.focus({ preventScroll: true });
        return;
      }
      target = closestDialogComponent(target)?.returnFocusTarget ?? null;
    }
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

/**
 * Page scroll lock shared by every open dialog: the page is fixed in place while at least one of them is
 * open, and goes back to where it was when the last one closes. Quick add is still closing when the cart
 * drawer opens, so the two overlap.
 *
 * @type {Set<object>}
 */
const scrollLockHolders = new Set();
let lockedScrollY = 0;

/**
 * @param {object} holder - The dialog taking the lock.
 */
function lockPageScroll(holder) {
  if (scrollLockHolders.has(holder)) return;

  if (scrollLockHolders.size === 0) {
    lockedScrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
  }

  scrollLockHolders.add(holder);
}

/**
 * @param {object} holder - The dialog releasing the lock.
 */
function unlockPageScroll(holder) {
  if (!scrollLockHolders.delete(holder) || scrollLockHolders.size > 0) return;

  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
}

/**
 * @param {HTMLElement} container - The element to search.
 * @returns {HTMLElement[]} Its focusable descendants that are rendered, in document order.
 */
function getFocusableElements(container) {
  return /** @type {HTMLElement[]} */ (
    Array.from(
      container.querySelectorAll(
        "a[href], button:enabled, input:not([type='hidden']):enabled, select:enabled, textarea:enabled, summary, [tabindex]:not([tabindex^='-'])"
      )
    )
  ).filter(isFocusableNow);
}

/**
 * @param {Element} element - The element to check.
 * @returns {element is HTMLElement} Whether the element is connected, rendered and not inert or in a closed dialog.
 */
function isFocusableNow(element) {
  return (
    element instanceof HTMLElement &&
    element.isConnected &&
    element.getClientRects().length > 0 &&
    !element.closest('[inert], dialog:not([open])')
  );
}

/**
 * @param {Element} element - The element to start from.
 * @returns {DialogComponent | null} The dialog component the element is in, if any.
 */
function closestDialogComponent(element) {
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (node instanceof DialogComponent) return node;
  }
  return null;
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        // Keep the lock while any other scroll-locking dialog or drawer is still open
        const open = document.querySelector('dialog[scroll-lock][open], details[scroll-lock][open]') !== null;

        document.documentElement.toggleAttribute('scroll-lock', open);
      }
    }
  },
  { capture: true }
);
